/**
 * SkillTreeNode — Tier 分级节点卡片（v4）
 *
 * 设计哲学：节点是视觉主角，大小代表战略重要性
 *
 * Tier 1（核心瓶颈，260×76）:
 *   ┌●●●──────────────────────────────┐
 *   │ HBM3E高带宽内存      ★★★★★ 72.3│  ← 大字号(15px) + 壁垒星级 + IPS
 *   │ SK海力士 · 三星           [N][G]│  ← Top公司 + 链路标记
 *   └────────────────────────────────┘
 *
 * Tier 2（关键组件，220×56）:
 *   ┌●●──────────────────────────┐
 *   │ 800G光模块           68.5  │  ← 中字号(13px) + IPS
 *   │ 中际旭创 · 新易盛     [N]  │
 *   └──────────────────────────┘
 *
 * Tier 3（支撑组件，180×42）:
 *   ┌●────────────────────┐
 *   │ 钨精矿        45.2  │  ← 小字号(11px) + IPS
 *   │ 厦门钨业            │
 *   └────────────────────┘
 */

import { memo, useContext, useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { BOTTLENECK_COLORS, CHAIN_COLORS, TYPE_LABELS } from '../../utils/chain-constants';
import { HighlightContext } from './HighlightContext';
import { useExplorationStore } from '../../stores/explorationStore';

export interface SkillTreeNodeData {
  label: string;
  nodeType: string;
  width: number;
  height: number;
  tier: 1 | 2 | 3;
  bottleneckLevel: 'high' | 'medium' | 'low';
  bottleneckReason?: string;
  chains: string[];
  isToll: boolean;
  isScarce: boolean;
  isIrreplaceable: boolean;
  companyCount: number;
  investAttractiveness: 'high' | 'medium' | 'low';
  hasInvestable: boolean;
  avgIps: number;
  topCompanies: string[];
  dimmed?: boolean;
  chainRole?: 'lead' | 'shared' | 'weak' | 'global';
  selected?: boolean;
  zoom?: number;
  isFocus?: boolean;  // path mode 下被选中的节点
}

/** 壁垒星级（基于 bottleneck_level + isToll） */
function getBarrierStars(bottleneckLevel: string, isToll: boolean): string {
  if (isToll && bottleneckLevel === 'high') return '★★★★★';
  if (bottleneckLevel === 'high') return '★★★★';
  if (bottleneckLevel === 'medium') return '★★★';
  return '★★';
}

const SkillTreeNode = memo(function SkillTreeNode({ id, data }: NodeProps<SkillTreeNodeData>) {
  const {
    label, width, height, tier, bottleneckLevel, bottleneckReason, nodeType,
    chains, isToll, companyCount,
    investAttractiveness, hasInvestable, avgIps, topCompanies,
    chainRole = 'global', isFocus = false,
  } = data;

  // hover 浮卡状态
  const [showHoverCard, setShowHoverCard] = useState(false);
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top');
  const [offsetX, setOffsetX] = useState(0);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nodeRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setShowHoverCard(true), 220);
  };
  const handleMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setShowHoverCard(false);
  };

  // 动态计算 placement + 水平偏移：避免边缘截断
  useLayoutEffect(() => {
    if (!showHoverCard || !nodeRef.current) return;
    const rect = nodeRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    // 垂直：比较上下剩余空间
    const spaceAbove = rect.top;
    const spaceBelow = viewportHeight - rect.bottom;
    setPlacement(spaceBelow > spaceAbove ? 'bottom' : 'top');
    // 水平：补偿溢出（浮卡 maxWidth 260，半宽 130）
    const cardHalfWidth = 130;
    const nodeCenter = rect.left + rect.width / 2;
    const overflowLeft = Math.max(0, cardHalfWidth - nodeCenter);
    const overflowRight = Math.max(0, nodeCenter + cardHalfWidth - viewportWidth);
    setOffsetX(overflowLeft - overflowRight);
  }, [showHoverCard]);

  // cleanup timer
  useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  // 从 Context 读取高亮状态（避免父级重建 nodes 数组）
  const ctx = useContext(HighlightContext);
  const selected = ctx?.selectedNodeId === id;
  const isCompanyRelated = !!ctx?.companyRelatedIds?.has(id);
  const isHighlighted = selected || isFocus || isCompanyRelated;
  const hasCompanySelection = !!ctx?.companyRelatedIds;
  const dimmed =
    (!!ctx?.neighborhoodIds && !ctx.neighborhoodIds.has(id)) ||
    (!!ctx?.searchMatchIds && !ctx.searchMatchIds.has(id)) ||
    (!!ctx?.filterMatchIds && !ctx.filterMatchIds.has(id)) ||
    (hasCompanySelection && !isCompanyRelated);

  // 探索状态标记（已勘察/观察/确信）— selector 订阅单节点状态，仅该节点状态变化时重渲染
  const explorationStatus = useExplorationStore(s => s.getStatus(id));
  const isConviction = explorationStatus === 'conviction';

  // 链路角色透明度
  const roleOpacity = chainRole === 'weak' ? 0.2 : chainRole === 'shared' ? 0.55 : 1;
  const opacity = dimmed ? 0.12 : roleOpacity;

  // 边框：瓶颈等级；选中/公司关联高亮时叠加金色描边
  const strokeColor = BOTTLENECK_COLORS[bottleneckLevel] || '#475569';
  const isGoldHighlight = selected || isCompanyRelated;
  const strokeWidth = isGoldHighlight ? (tier === 1 ? 3 : 2.5) : (tier === 1 ? 2.5 : tier === 2 ? 1.5 : 1);
  const effectiveStrokeColor = isGoldHighlight ? '#fbbf24' : strokeColor;

  // 背景：投资吸引力；选中/公司关联高亮时叠加金色背景
  const bgColor = isGoldHighlight
    ? 'rgba(251,191,36,0.22)'
    : investAttractiveness === 'high' ? 'rgba(251,191,36,0.16)' :
      investAttractiveness === 'medium' ? 'rgba(59,130,246,0.10)' :
      'rgba(15,23,42,0.75)';

  // 链路色点（最多3个）
  const chainDots = chains.slice(0, 3);

  // 公司名显示
  const maxCompanies = tier === 1 ? 2 : tier === 2 ? 2 : 1;
  const companyText = topCompanies.slice(0, maxCompanies).join(' · ');

  // IPS 显示
  const ipsText = avgIps > 0 ? avgIps.toFixed(1) : '';

  // 字号根据 Tier
  const nameFontSize = tier === 1 ? 15 : tier === 2 ? 13 : 11;
  const companyFontSize = tier === 1 ? 11 : tier === 2 ? 10 : 9;
  const ipsFontSize = tier === 1 ? 13 : tier === 2 ? 11 : 10;

  // 壁垒星级（仅 Tier 1 显示）
  const barrierStars = tier === 1 ? getBarrierStars(bottleneckLevel, isToll) : '';

  return (
    <div
      ref={nodeRef}
      className={`skill-tree-node${isHighlighted ? ' selected' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        width,
        height,
        opacity,
        cursor: 'pointer',
        transition: 'opacity 0.2s, transform 0.15s, box-shadow 0.15s',
        transform: isHighlighted ? 'scale(1.05)' : 'scale(1)',
        background: bgColor,
        border: `${strokeWidth}px solid ${effectiveStrokeColor}`,
        borderRadius: tier === 1 ? 8 : 6,
        display: 'flex',
        alignItems: 'center',
        overflow: 'visible',
        position: 'relative',
        boxShadow: isConviction
          ? `0 0 0 3px #fbbf24, 0 0 20px rgba(251,191,36,0.4), 0 ${tier === 1 ? 6 : 4}px ${tier === 1 ? 16 : 12}px rgba(0,0,0,0.5)`
          : isHighlighted
            ? `0 0 0 2px #10b981, 0 ${tier === 1 ? 6 : 4}px ${tier === 1 ? 16 : 12}px rgba(0,0,0,0.5)`
            : `0 1px ${tier === 1 ? 5 : 3}px rgba(0,0,0,0.25)`,
      }}
    >
      {/* hover 浮卡 — 220ms 延迟，只显示"为什么重要"，与 DetailPanel 信息错位 */}
      {showHoverCard && (
        <div style={{
          position: 'absolute',
          ...(placement === 'top' ? { bottom: '100%', marginBottom: 8 } : { top: '100%', marginTop: 8 }),
          left: '50%',
          transform: `translateX(calc(-50% + ${offsetX}px))`,
          background: 'rgba(15,23,42,0.97)',
          border: '1px solid #334155',
          borderRadius: 8,
          padding: '10px 12px',
          minWidth: 200,
          maxWidth: 260,
          boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
          zIndex: 100,
          pointerEvents: 'none',
          fontSize: 11,
          color: '#cbd5e1',
          lineHeight: 1.5,
          animation: 'hoverCardFadeIn 0.12s ease-out',
        }}>
          {/* 标题行 */}
          <div style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>{label}</div>
          {/* 瓶颈原因 — hover 浮卡的核心差异化信息 */}
          {bottleneckReason ? (
            <div style={{
              color: '#fbbf24', fontSize: 10, lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {bottleneckReason}
            </div>
          ) : (
            <div style={{ color: '#64748b', fontSize: 10 }}>
              {TYPE_LABELS[nodeType] || nodeType}{isToll ? ' · 收费站节点' : ''}
            </div>
          )}
          {/* 小三角箭头 */}
          <div style={{
            position: 'absolute',
            ...(placement === 'top'
              ? { top: '100%', borderTop: '6px solid rgba(15,23,42,0.97)' }
              : { bottom: '100%', borderBottom: '6px solid rgba(15,23,42,0.97)' }),
            left: '50%',
            transform: `translateX(calc(-50% - ${offsetX}px))`,
            width: 0, height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
          }} />
        </div>
      )}
      {/* 左侧链路色点区 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tier === 1 ? 3 : 2,
        padding: tier === 1 ? '0 8px 0 9px' : '0 6px 0 7px',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        height: '100%',
      }}>
        {chainDots.length > 0 ? chainDots.map(ch => (
          <span key={ch} style={{
            width: tier === 1 ? 8 : 6,
            height: tier === 1 ? 8 : 6,
            borderRadius: '50%',
            background: CHAIN_COLORS[ch] || '#475569',
            boxShadow: `0 0 ${tier === 1 ? 5 : 3}px ${CHAIN_COLORS[ch] || '#475569'}80`,
            flexShrink: 0,
          }} />
        )) : (
          <span style={{
            width: tier === 1 ? 8 : 6,
            height: tier === 1 ? 8 : 6,
            borderRadius: '50%',
            background: '#334155',
            flexShrink: 0,
          }} />
        )}
      </div>

      {/* 主内容区 */}
      <div style={{
        flex: 1,
        padding: tier === 1 ? '5px 8px 5px 5px' : '3px 6px 3px 4px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minWidth: 0,
        gap: tier === 1 ? 2 : 1,
      }}>
        {/* 第一行：节点名 + 壁垒星级 + IPS */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          minWidth: 0,
        }}>
          <span style={{
            fontSize: nameFontSize,
            fontWeight: tier === 1 ? 800 : tier === 2 ? 700 : 600,
            color: investAttractiveness === 'high' ? '#fbbf24' : '#e2e8f0',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 1,
            minWidth: 0,
          }}>
            {label}
          </span>
          {/* 壁垒星级（仅 Tier 1） */}
          {barrierStars && (
            <span style={{
              fontSize: 10,
              color: '#f59e0b',
              flexShrink: 0,
              letterSpacing: -0.5,
            }} title="壁垒等级">
              {barrierStars}
            </span>
          )}
          {/* IPS 分数 */}
          {ipsText && (
            <span style={{
              fontSize: ipsFontSize,
              fontWeight: 800,
              color: investAttractiveness === 'high' ? '#fbbf24' : '#60a5fa',
              flexShrink: 0,
              marginLeft: 'auto',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {ipsText}
            </span>
          )}
          {/* Toll 标记 */}
          {isToll && (
            <span style={{ fontSize: tier === 1 ? 13 : 11, flexShrink: 0 }} title="收费站">👑</span>
          )}
        </div>

        {/* 第二行：Top公司 + 可投资标记 */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          minWidth: 0,
        }}>
          {companyText ? (
            <span style={{
              fontSize: companyFontSize,
              color: hasInvestable ? '#86efac' : '#64748b',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexShrink: 1,
              minWidth: 0,
            }}>
              {companyText}
              {companyCount > maxCompanies && <span style={{ color: '#475569' }}> +{companyCount - maxCompanies}</span>}
            </span>
          ) : (
            <span style={{
              fontSize: companyFontSize,
              color: '#475569',
              flexShrink: 1,
            }}>
              {companyCount > 0 ? `${companyCount}家公司` : '—'}
            </span>
          )}
          {/* 可投资绿点 */}
          {hasInvestable && (
            <span style={{
              width: tier === 1 ? 6 : 5,
              height: tier === 1 ? 6 : 5,
              borderRadius: '50%',
              background: '#22c55e',
              flexShrink: 0,
              boxShadow: '0 0 3px rgba(34,197,94,0.6)',
              marginLeft: 'auto',
            }} title="有可投资标的" />
          )}
        </div>
      </div>

      {/* 右侧瓶颈色条（高瓶颈时显示） */}
      {bottleneckLevel === 'high' && (
        <div style={{
          position: 'absolute',
          right: 0, top: 0, bottom: 0,
          width: tier === 1 ? 4 : 3,
          background: strokeColor,
          boxShadow: '0 0 6px rgba(239,68,68,0.4)',
        }} />
      )}

      {/* 探索状态：左侧色条（观察=绿 / 确信=金） */}
      {(explorationStatus === 'watchlist' || explorationStatus === 'conviction') && (
        <div style={{
          position: 'absolute',
          left: 0, top: 0, bottom: 0,
          width: 3,
          background: explorationStatus === 'conviction' ? '#fbbf24' : '#22c55e',
          boxShadow: explorationStatus === 'conviction'
            ? '0 0 6px rgba(251,191,36,0.6)'
            : '0 0 4px rgba(34,197,94,0.5)',
          zIndex: 2,
        }} />
      )}

      {/* 探索状态：右上角标记（已勘察✓ / 确信◆） */}
      {(explorationStatus === 'explored' || explorationStatus === 'conviction') && (
        <div style={{
          position: 'absolute',
          top: 1,
          right: bottleneckLevel === 'high' ? 7 : 3,
          fontSize: explorationStatus === 'conviction' ? 11 : 9,
          fontWeight: 700,
          color: explorationStatus === 'conviction' ? '#fbbf24' : '#94a3b8',
          textShadow: '0 1px 2px rgba(0,0,0,0.9)',
          zIndex: 3,
          lineHeight: 1,
        }} title={
          explorationStatus === 'conviction' ? '确信名单' : '已勘察'
        }>
          {explorationStatus === 'conviction' ? '◆' : '✓'}
        </div>
      )}

      <Handle type="target" position={Position.Left} style={{ opacity: 0, pointerEvents: 'none' }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0, pointerEvents: 'none' }} />
    </div>
  );
});

export default SkillTreeNode;
